import { Guia } from "./types";

export const GUIAS: Guia[] = [
  {
    slug: "que-revisar-antes-de-firmar-una-renta",
    titulo: "Qué revisar antes de firmar una renta",
    resumen:
      "Un contrato de renta mal leído se paga meses después: depósitos retenidos, cláusulas imposibles y salidas costosas. Esta guía te da la lista de verificación esencial.",
    lecturaMin: 6,
    momentoRelacionado: "vida-independiente",
    servicioRelacionado: "bienes-y-patrimonio",
    secciones: [
      {
        titulo: "El contrato: lo que debe decir claramente",
        parrafos: [
          "Un buen contrato de renta no deja nada al aire. Si algo se acordó de palabra, debe estar por escrito.",
        ],
        lista: [
          "Nombre completo y datos de quien arrienda, y confirmación de que es el propietario o tiene facultades",
          "Dirección exacta y completa del inmueble",
          "Monto de la renta, fecha de pago, forma de pago y comprobantes",
          "Duración del contrato y condiciones de renovación",
          "Monto del depósito y condiciones de devolución",
          "Quién paga qué: mantenimiento, servicios, reparaciones",
        ],
      },
      {
        titulo: "Antes de firmar: verifica esto",
        parrafos: ["Veinte minutos de verificación evitan la mayoría de los problemas de renta."],
        lista: [
          "Estado del inmueble documentado con inventario y fotos con fecha",
          "Recibo del depósito con nombre, monto y fecha",
          "Cláusulas de salida anticipada y qué pasa si te vas antes",
          "Si hay aumentos previstos, cómo y cuándo aplican",
          "Quién responde por reparaciones estructurales",
        ],
      },
      {
        titulo: "Señales de alerta",
        lista: [
          "Pagos solo en efectivo, sin recibo ni comprobante",
          "Depósitos muy por encima de un mes sin justificación",
          "Cláusulas donde renuncias a derechos que la ley no permite renunciar",
          "‘Lo arreglamos por fuera, no hace falta ponerlo en el contrato’",
        ],
      },
      {
        titulo: "Si ya firmaste",
        parrafos: [
          "Guarda el contrato, todos los comprobantes de pago y la comunicación con el arrendador. Con esa base, la mayoría de los conflictos de renta se pueden negociar o reclamar con buen pie. Si el problema ya empezó, no dejes que corran los meses: la evidencia fresca pesa más.",
        ],
      },
    ],
    seoTitle: "Qué revisar antes de firmar una renta",
    seoDesc:
      "Lista de verificación antes de firmar un contrato de renta: depósito, cláusulas, señales de alerta y qué hacer si ya firmaste.",
  },
  {
    slug: "que-preparar-para-una-consulta-sobre-separacion",
    titulo: "Qué preparar para una consulta sobre separación",
    resumen:
      "Llegar preparado a una consulta de separación cambia todo: más claridad, menos desgaste y mejores decisiones desde la primera conversación.",
    lecturaMin: 7,
    momentoRelacionado: "cambios-de-vida",
    servicioRelacionado: "familia-y-acuerdos",
    secciones: [
      {
        titulo: "Fechas y documentos básicos",
        lista: [
          "Acta de matrimonio o constancia de unión, si la hay",
          "Identificaciones oficiales de las partes",
          "Domicilios que han tenido la familia, anteriores y actuales",
          "Fechas clave: inicio de la relación, matrimonio, nacimientos, separación",
        ],
      },
      {
        titulo: "Situación económica",
        parrafos: [
          "No necesitas tener números exactos, pero sí una foto honesta del panorama económico.",
        ],
        lista: [
          "Ingresos aproximados de cada quien",
          "Bienes comprados juntos y a nombre de quién están",
          "Deudas: con quién, por cuánto y a nombre de quién",
          "Cuentas conjuntas o compartidas",
        ],
      },
      {
        titulo: "Si hay hijas e hijos",
        lista: [
          "Edades y escuela o actividades que mantienen una rutina",
          "Cómo es hoy la convivencia y el cuidado del día a día",
          "Gastos regulares de las y los menores",
          "Propuestas realistas de convivencia que ya funcionen",
        ],
      },
      {
        titulo: "Qué esperar de la primera consulta",
        parrafos: [
          "No necesitas llegar con decisiones tomadas. La primera conversación sirve para entender las rutas posibles —por acuerdo o por proceso—, qué documentos faltan y qué tiempos son realistas. Trae tus preguntas por escrito: es la mejor forma de aprovechar el tiempo sin olvidar lo importante.",
        ],
      },
    ],
    seoTitle: "Qué preparar para una consulta sobre separación",
    seoDesc:
      "Documentos, información económica y expectativas para una primera consulta sobre separación o divorcio.",
  },
  {
    slug: "que-documentos-conservar-ante-un-problema-laboral",
    titulo: "Qué documentos conservar ante un problema laboral",
    resumen:
      "En un conflicto laboral, la evidencia decide. Esta guía te dice qué guardar, cómo organizarlo y qué no hacer nunca mientras el problema se resuelve.",
    lecturaMin: 6,
    momentoRelacionado: "trabajo-y-negocio",
    servicioRelacionado: "defensa-laboral",
    secciones: [
      {
        titulo: "Lo que siempre conviene conservar",
        lista: [
          "Contrato de trabajo y cualquier anexo o modificación",
          "Recibos de pago o comprobantes de transferencia",
          "Cartas, correos y mensajes de trabajo relacionados con tu situación",
          "Horarios, evidencia de horas trabajadas y descansos",
          "Comprobantes de prestaciones: vales, seguro, aguinaldo",
        ],
      },
      {
        titulo: "Si te despiden o te piden renunciar",
        lista: [
          "No firmes nada bajo presión, especialmente una ‘liquidación total’",
          "Pide que todo te lo entreguen por escrito",
          "Guarda la carta de terminación o de renuncia, si existe",
          "Anota fechas, nombres y testigos de lo que pasó",
        ],
      },
      {
        titulo: "Tiempos que importan",
        parrafos: [
          "Las reclamaciones laborales tienen plazos que corren rápido. Entre antes consultes, más opciones quedan sobre la mesa. Y recuerda: nunca borres mensajes originales, ni en tu teléfono ni en el correo del trabajo.",
        ],
      },
      {
        titulo: "Cómo organizar tu carpeta",
        lista: [
          "Carpeta digital y una física, con lo mismo",
          "Archivos nombrados con fecha: AAAA-MM-DD y una descripción corta",
          "Copias de seguridad en un lugar que no dependa del trabajo",
        ],
      },
    ],
    seoTitle: "Qué documentos conservar ante un problema laboral",
    seoDesc:
      "Evidencia laboral: contratos, pagos, mensajes y plazos para defender tus derechos ante un despido o adeudo.",
  },
  {
    slug: "que-revisar-antes-de-asociarte",
    titulo: "Qué revisar antes de asociarte",
    resumen:
      "Asociarse es fácil; separarse es lo difícil. Antes de firmar una sociedad, esta es la revisión mínima que protege tu dinero y la amistad.",
    lecturaMin: 7,
    momentoRelacionado: "trabajo-y-negocio",
    servicioRelacionado: "asesoria-de-negocios",
    secciones: [
      {
        titulo: "Qué revisar del negocio",
        lista: [
          "Actividad real del negocio y sus permisos",
          "Deudas, juicios y contratos vigentes",
          "Quién tiene firmado qué, a nombre de quién",
          "Los números: estados de cuenta y compromisos de pago",
        ],
      },
      {
        titulo: "El acuerdo entre socios",
        parrafos: [
          "El acuerdo entre socios es el documento más importante de la sociedad, más incluso que el acta constitutiva. Debe contestar, al menos, estas preguntas:",
        ],
        lista: [
          "Cuánto aporta cada quién y qué recibe a cambio",
          "Quién decide qué, y cómo se resuelven los empates",
          "Cómo se reparten utilidades y cuándo se sacan",
          "Qué pasa si un socio quiere salir, o no puede seguir",
          "Qué pasa si un socio muere o queda incapacitado",
        ],
      },
      {
        titulo: "Señales para frenar",
        lista: [
          "Prisa por firmar: ‘el detalle lo arreglamos después’",
          "Promesas importantes que solo existen de palabra",
          "Nadie quiere poner por escrito cómo se sale",
          "Los números del negocio solo los ve una persona",
        ],
      },
      {
        titulo: "Lo mínimo que debería quedar por escrito",
        parrafos: [
          "Aportaciones, decisiones, utilidades, salida de socios y mecanismo de resolución de conflictos. Si algo de eso no está escrito, todavía no es una sociedad: es una espera de problema.",
        ],
      },
    ],
    seoTitle: "Qué revisar antes de asociarte",
    seoDesc:
      "Revisión previa para socios: aportaciones, decisiones, utilidades, salida de socios y señales de alerta.",
  },
  {
    slug: "como-organizar-documentos-importantes",
    titulo: "Cómo organizar documentos importantes",
    resumen:
      "Un robo, una inundación o una urgencia médica: cuando hacen falta los documentos, es tarde para buscarlos. Cinco carpetas bastan para tenerlo todo en orden.",
    lecturaMin: 5,
    momentoRelacionado: "cambios-de-vida",
    servicioRelacionado: "bienes-y-patrimonio",
    secciones: [
      {
        titulo: "Los cinco grupos de documentos",
        lista: [
          "Identidad y actas: identificaciones, actas de nacimiento, matrimonio",
          "Vivienda y propiedad: escrituras, contratos, pagos, predial",
          "Trabajo: contratos, recibos, cartas, prestaciones",
          "Dinero y seguros: pólizas, cuentas, deudas, beneficiarios",
          "Salud y cuidados: padecimientos, voluntades anticipadas, contactos",
        ],
      },
      {
        titulo: "Digital + físico",
        parrafos: [
          "Todo documento importante debe existir dos veces: original físico en un lugar seco y seguro, y copia digital en la nube con acceso de una persona de confianza. Nombra los archivos con fecha y descripción: AAAA-MM-DD y tres o cuatro palabras bastan.",
        ],
      },
      {
        titulo: "Revisión una vez al año",
        lista: [
          "Marca un día fijo al año para revisar las cinco carpetas",
          "Actualiza lo que cambió: beneficiarios, direcciones, estados",
          "Descarta con cuidado: nunca tires actas ni escrituras, aunque estén viejas",
        ],
      },
      {
        titulo: "Qué compartir y qué no",
        parrafos: [
          "Para trámites casi siempre basta una copia; manda copias con marca de agua y reserva el original para quien realmente deba verlo. Nunca compartas documentos de identidad por mensajería sin saber quién los pide.",
        ],
      },
    ],
    seoTitle: "Cómo organizar documentos importantes",
    seoDesc:
      "Cinco carpetas para ordenar tus documentos importantes: identidad, vivienda, trabajo, dinero y salud.",
  },
];

export const guiaBySlug = (slug?: string) => GUIAS.find((g) => g.slug === slug);
